import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import apiService from "../../../../service/Api";


type EmojiRating = 1 | 2 | 3 | 4 | 5;
type RecommendOption = "yes" | "maybe" | "no";

interface ReviewPayload {
  reference_number: string;
  client_review: EmojiRating;
  improvement_suggestions: string[];
  comment: string;
  would_recommend: RecommendOption | null;
  recommendation_reason: string;
}


const EMOJI_OPTIONS: {
  value: EmojiRating;
  emoji: string;
  label: string;
  color: string;
}[] = [
  { value: 1, emoji: "😡", label: "Very Poor", color: "#ef4444" },
  { value: 2, emoji: "😕", label: "Poor", color: "#f97316" },
  { value: 3, emoji: "😐", label: "Average", color: "#eab308" },
  { value: 4, emoji: "😊", label: "Good", color: "#84cc16" },
  { value: 5, emoji: "🤩", label: "Excellent", color: "#22c55e" },
];

const IMPROVEMENT_TAGS = [
  "Communication",
  "Timing",
  "Service quality",
  "Staff professionalism",
  "Pricing",
  "Other",
];

const RECOMMEND_OPTIONS: RecommendOption[] = ["yes", "maybe", "no"];

const BRAND = "#5c3a2e";
const BRAND_LIGHT = "#f5ede9";


const ProgressBar = ({ step, total }: { step: number; total: number }) => (
  <Box
    sx={{
      width: "100%",
      height: 4,
      bgcolor: "#e8ddd9",
      borderRadius: 99,
      mb: 3,
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        height: "100%",
        width: `${(step / total) * 100}%`,
        bgcolor: BRAND,
        borderRadius: 99,
        transition: "width 0.4s ease",
      }}
    />
  </Box>
);


const ReviewHeader = () => (
  <Stack alignItems="center" mb={3}>
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: 3,
        bgcolor: BRAND_LIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 1.5,
        fontSize: 24,
      }}
    >
      ⭐
    </Box>
    <Typography
      sx={{ fontSize: 20, fontWeight: 700, color: "#1e293b", mb: 0.5 }}
    >
      Rate Your Experience
    </Typography>
    <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center" }}>
      We appreciate your feedback. This will only take a few seconds.
    </Typography>
  </Stack>
);


const ReviewCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      minHeight: "100vh",
      bgcolor: "#f1f5f9",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: 2,
    }}
  >
    <Box
      sx={{
        bgcolor: "#ffffff",
        borderRadius: 4,
        p: { xs: 3, sm: 4 },
        width: "100%",
        maxWidth: 480,
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
      }}
    >
      {children}
    </Box>
  </Box>
);

const Step1 = ({
  selected,
  onSelect,
}: {
  selected: EmojiRating | null;
  onSelect: (v: EmojiRating) => void;
}) => (
  <>
    <Typography
      sx={{ fontSize: 14, color: "#64748b", textAlign: "center", mb: 2.5 }}
    >
      How was your experience with our service?
    </Typography>
    <Stack direction="row" justifyContent="center" spacing={2}>
      {EMOJI_OPTIONS.map(({ value, emoji, label, color }) => {
        const isSelected = selected === value;
        return (
          <Stack
            key={value}
            alignItems="center"
            spacing={0.5}
            onClick={() => onSelect(value)}
            sx={{ cursor: "pointer" }}
          >
            <Box
              sx={{
                fontSize: 36,
                lineHeight: 1,
                filter: isSelected ? "none" : "grayscale(60%) opacity(0.6)",
                transform: isSelected ? "scale(1.2)" : "scale(1)",
                transition: "all 0.2s ease",
              }}
            >
              {emoji}
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                color: isSelected ? color : "#94a3b8",
                fontWeight: isSelected ? 600 : 400,
                transition: "color 0.2s",
              }}
            >
              {label}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  </>
);


const Step2 = ({
  selectedTags,
  onToggleTag,
  comment,
  onCommentChange,
  onContinue,
}: {
  selectedTags: string[];
  onToggleTag: (tag: string) => void;
  comment: string;
  onCommentChange: (v: string) => void;
  onContinue: () => void;
}) => (
  <>
    <Typography
      sx={{ fontSize: 14, color: "#64748b", textAlign: "center", mb: 2 }}
    >
      What could we improve?
    </Typography>
    <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="center" mb={2.5}>
      {IMPROVEMENT_TAGS.map((tag) => {
        const active = selectedTags.includes(tag);
        return (
          <Chip
            key={tag}
            label={tag}
            onClick={() => onToggleTag(tag)}
            sx={{
              bgcolor: active ? BRAND_LIGHT : "transparent",
              color: active ? BRAND : "#64748b",
              border: `1px solid ${active ? BRAND : "#e2e8f0"}`,
              fontWeight: active ? 600 : 400,
              fontSize: 13,
              cursor: "pointer",
              "&:hover": { bgcolor: BRAND_LIGHT, color: BRAND },
              transition: "all 0.15s",
            }}
          />
        );
      })}
    </Stack>
    <TextField
      fullWidth
      multiline
      rows={3}
      placeholder="Any additional information"
      value={comment}
      onChange={(e) => onCommentChange(e.target.value)}
      sx={{
        mb: 3,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2.5,
          fontSize: 13,
          color: "#475569",
          "& fieldset": { borderColor: "#e2e8f0" },
          "&:hover fieldset": { borderColor: "#cbd5e1" },
          "&.Mui-focused fieldset": { borderColor: BRAND },
        },
      }}
    />
    <Button
      fullWidth
      variant="contained"
      onClick={onContinue}
      sx={{
        bgcolor: BRAND,
        color: "#fff",
        borderRadius: 2,
        py: 1.2,
        fontSize: 14,
        fontWeight: 600,
        textTransform: "none",
        "&:hover": { bgcolor: "#4a2e24" },
      }}
    >
      Continue
    </Button>
  </>
);


const Step3 = ({
  selected,
  onSelect,
  recommendationReason,
  onReasonChange,
  onSubmit,
  loading,
}: {
  selected: RecommendOption | null;
  onSelect: (v: RecommendOption) => void;
  recommendationReason: string;
  onReasonChange: (v: string) => void;
  onSubmit: () => void;
  loading: boolean;
}) => (
  <>
    <Typography
      sx={{ fontSize: 14, color: "#64748b", textAlign: "center", mb: 2.5 }}
    >
      Would you recommend our service to others?
    </Typography>
    <Stack direction="row" spacing={1.5} justifyContent="center" mb={2.5}>
      {RECOMMEND_OPTIONS.map((opt) => {
        const active = selected === opt;
        return (
          <Button
            key={opt}
            onClick={() => onSelect(opt)}
            variant="outlined"
            sx={{
              borderColor: active ? BRAND : "#e2e8f0",
              color: active ? BRAND : "#64748b",
              bgcolor: active ? BRAND_LIGHT : "transparent",
              borderRadius: 99,
              px: 3,
              py: 0.8,
              fontSize: 13,
              fontWeight: active ? 600 : 400,
              textTransform: "capitalize",
              "&:hover": { borderColor: BRAND, bgcolor: BRAND_LIGHT, color: BRAND },
              transition: "all 0.15s",
            }}
          >
            {opt}
          </Button>
        );
      })}
    </Stack>
    <TextField
      fullWidth
      multiline
      rows={3}
      placeholder="Tell us why (optional)"
      value={recommendationReason}
      onChange={(e) => onReasonChange(e.target.value)}
      sx={{
        mb: 3,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2.5,
          fontSize: 13,
          color: "#475569",
          "& fieldset": { borderColor: "#e2e8f0" },
          "&:hover fieldset": { borderColor: "#cbd5e1" },
          "&.Mui-focused fieldset": { borderColor: BRAND },
        },
      }}
    />
    <Button
      fullWidth
      variant="contained"
      onClick={onSubmit}
      disabled={loading || !selected}
      sx={{
        bgcolor: BRAND,
        color: "#fff",
        borderRadius: 2,
        py: 1.2,
        fontSize: 14,
        fontWeight: 600,
        textTransform: "none",
        "&:hover": { bgcolor: "#4a2e24" },
        "&:disabled": { bgcolor: "#c8b0a8", color: "#fff" },
      }}
    >
      {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit Feedback"}
    </Button>
  </>
);


const Step4 = () => (
  <Stack alignItems="center" py={2}>
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        bgcolor: "#fff7ed",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 36,
        mb: 2,
      }}
    >
      🎉
    </Box>
    <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1e293b", mb: 1 }}>
      Thank You for Your Feedback
    </Typography>
    <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center" }}>
      Your feedback helps us improve our services and support our clients better.
    </Typography>
  </Stack>
);


export default function ClientReviewFlow() {
  const { reference_number } = useParams<{ reference_number: string }>();

  const [step, setStep] = useState(1);
  const [rating, setRating] = useState<EmojiRating | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [recommend, setRecommend] = useState<RecommendOption | null>(null);
  const [recommendationReason, setRecommendationReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSelectRating = (value: EmojiRating) => {
    setRating(value);
    setTimeout(() => setStep(2), 300);
  };

  const handleSubmit = async () => {
    if (!rating || !recommend || !reference_number) return;

    const payload: ReviewPayload = {
      reference_number,
      client_review: rating,
      improvement_suggestions: selectedTags,
      comment,
      would_recommend: recommend,
      recommendation_reason: recommendationReason,
    };

    try {
      setLoading(true);
      setError(null);
      await apiService.post("/api/bookings/booking-review/", payload);
      setStep(4);
    } catch (err) {
      console.error("Failed to submit review", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (step === 4) {
    return (
      <ReviewCard>
        <Step4 />
      </ReviewCard>
    );
  }

  return (
    <ReviewCard>
      <ReviewHeader />
      <ProgressBar step={step - 1} total={3} />

      {step === 1 && (
        <Step1 selected={rating} onSelect={handleSelectRating} />
      )}

      {step === 2 && (
        <Step2
          selectedTags={selectedTags}
          onToggleTag={toggleTag}
          comment={comment}
          onCommentChange={setComment}
          onContinue={() => setStep(3)}
        />
      )}

      {step === 3 && (
        <Step3
          selected={recommend}
          onSelect={setRecommend}
          recommendationReason={recommendationReason}
          onReasonChange={setRecommendationReason}
          onSubmit={handleSubmit}
          loading={loading}
        />
      )}

      {error && (
        <Typography sx={{ color: "#ef4444", fontSize: 12, textAlign: "center", mt: 1.5 }}>
          {error}
        </Typography>
      )}
    </ReviewCard>
  );
}